import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Outlet, useNavigate } from 'react-router-dom';

import { TFunction } from 'i18next';

import { ArrowLeftGreenIcon } from 'assets';
import { Logo, StyledButton, UserDropdown } from 'components/shared';
import LanguageDropdown from 'components/shared/LanguageDropdown/LanguageDropdown';
import { useBannersDetails } from 'constants/banners';
import {
   CLIENT_ORDERS,
   CLIENT_PALLETS,
   CLIENT_PALLETS_TRANSFER_HISTORY,
   DISTRIBUTION_CENTER_PALLET_BALANCE,
   DISTRIBUTION_CENTER_PALLET_PICKUPS,
   DISTRIBUTION_CENTER_PALLETS_TRANSFER_HISTORY,
   STORAGE_PALLET_BALANCE,
   STORAGE_PALLETS_TRANSFER_HISTORY,
   SUPPLIER_CLIENT_PALLET_BALANCES,
   SUPPLIER_CLIENTS,
   SUPPLIER_COMMERCIAL_NETWORKS,
   SUPPLIER_EXTERNAL_STORAGES,
   SUPPLIER_INTERNAL_STORAGES,
   SUPPLIER_ORDERS,
   SUPPLIER_PALLETS,
   SUPPLIER_PALLETS_TRANSFER_HISTORY,
} from 'constants/routes';
import { BannerData } from 'interfaces';
import { Banner, TopBar } from 'storybook';
import { errorHandler } from 'utils/errorHandler';

import SideBar from './SideBar/SideBar';
import styles from './Layout.module.css';

const pagesWithTopBarHeader = [
   CLIENT_ORDERS,
   CLIENT_PALLETS,
   CLIENT_PALLETS_TRANSFER_HISTORY,
   SUPPLIER_ORDERS,
   SUPPLIER_CLIENTS,
   SUPPLIER_INTERNAL_STORAGES,
   SUPPLIER_EXTERNAL_STORAGES,
   SUPPLIER_COMMERCIAL_NETWORKS,
   SUPPLIER_CLIENT_PALLET_BALANCES,
   SUPPLIER_PALLETS,
   SUPPLIER_PALLETS_TRANSFER_HISTORY,
   DISTRIBUTION_CENTER_PALLET_PICKUPS,
   DISTRIBUTION_CENTER_PALLET_BALANCE,
   DISTRIBUTION_CENTER_PALLETS_TRANSFER_HISTORY,
   STORAGE_PALLET_BALANCE,
   STORAGE_PALLETS_TRANSFER_HISTORY,
];

const TOP_BAR_HEADER_LABELS = (t: TFunction): { [key: string]: string } => ({
   [CLIENT_ORDERS]: t('nav.orders'),
   [CLIENT_PALLETS]: t('headers.pallets'),
   [CLIENT_PALLETS_TRANSFER_HISTORY]: t('headers.pallets_transfer_history'),
   [SUPPLIER_ORDERS]: t('nav.orders'),
   [SUPPLIER_CLIENTS]: t('nav.supplier_clients'),
   [SUPPLIER_INTERNAL_STORAGES]: t('nav.supplier_internal_storages'),
   [SUPPLIER_EXTERNAL_STORAGES]: t('nav.supplier_internal_storages'),
   [SUPPLIER_COMMERCIAL_NETWORKS]: t('nav.supplier_internal_storages'),
   [SUPPLIER_CLIENT_PALLET_BALANCES]: t('nav.supplier_internal_storages'),
   [SUPPLIER_PALLETS]: t('headers.supplier_pallets'),
   [SUPPLIER_PALLETS_TRANSFER_HISTORY]: t('headers.pallets_transfer_history'),
   [DISTRIBUTION_CENTER_PALLET_PICKUPS]: t('nav.distribution_center_pallet_pickups'),
   [DISTRIBUTION_CENTER_PALLET_BALANCE]: t('nav.distribution_center_pallet_balance'),
   [DISTRIBUTION_CENTER_PALLETS_TRANSFER_HISTORY]: t('headers.pallets_transfer_history'),
   [STORAGE_PALLET_BALANCE]: t('nav.distribution_center_pallet_balance'),
   [STORAGE_PALLETS_TRANSFER_HISTORY]: t('headers.pallets_transfer_history'),
});

const Layout = () => {
   const [bannerData, setBannerData] = useState<BannerData | null>(null);
   const location = useLocation();
   const navigate = useNavigate();

   const { t } = useTranslation();
   const { LOGOUT_ERROR_BANNER } = useBannersDetails();

   const handleLogoutError = (error: unknown) =>
      errorHandler(error, () => setBannerData(LOGOUT_ERROR_BANNER));

   return (
      <div className={styles.layoutContainer}>
         <SideBar />
         <div className={styles.contentContainer}>
            <TopBar
               className={styles.topBar}
               contentLeft={
                  pagesWithTopBarHeader.includes(location.pathname) ? (
                     <>
                        <Logo className={styles.mobileOnlyLogo} />
                        <h2 className={styles.topBarHeader}>
                           {TOP_BAR_HEADER_LABELS(t)[location.pathname]}
                        </h2>
                     </>
                  ) : (
                     <StyledButton
                        onClick={() => {
                           navigate(-1);
                        }}
                        icon={<ArrowLeftGreenIcon />}
                        text={t('common.back')}
                        variant="text"
                     />
                  )
               }
               contentRight={
                  <div className={styles.contentRightContainer}>
                     <LanguageDropdown />
                     <UserDropdown onLogoutError={handleLogoutError} />
                  </div>
               }
            />
            <div className={styles.childrenContainer}>
               {bannerData && (
                  <Banner
                     variant={bannerData.variant}
                     title={bannerData.title}
                     children={bannerData.description}
                     className={styles.banner}
                     onClose={() => setBannerData(null)}
                     withCloseIcon
                     fullWidth
                  />
               )}
               <Outlet />
            </div>
         </div>
      </div>
   );
};

export default Layout;
